<template>
  <component
    :is="sortable ? 'Draggable' : 'div'"
    class="c-item-grid"
    :class="wrapperClass"
    :style="gridStyle"
    v-bind="wrapperProps"
    v-on="wrapperListeners"
  >
    <GridItem
      v-for="item in items"
      :key="item.id"
      :item="item"
      :is-selected="isSelected(item)"
      v-bind="propsFor(item)"
      @click="$emit('item-click', $event)"
      @dblclick="$emit('item-dblclick', $event)"
      @open-preview="$emit('open-preview', $event)"
      @download="$emit('download', $event)"
      @edit="$emit('edit', $event)"
      @count-change="$emit('item-count-change', { item, delta: $event })"
      @copy-to-file-browser="$emit('copy-to-file-browser', $event)"
      @action="$emit('item-action', $event)"
      @dragstart="$emit('item-dragstart', { event: $event, item })"
      @drop.stop="$emit('item-drop', { event: $event, item })"
    />
  </component>
</template>

<script>
import Draggable from 'vuedraggable';
import GridItem from '@frontend/components/modules/file-browser/browser/GridItem.vue';

export default {
  name: 'ItemGrid',
  components: {
    Draggable,
    GridItem,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    isSelected: {
      type: Function,
      default: () => false,
    },
    // A number pins the column count; null switches to fluid auto-fill at `minTileWidth`.
    columns: {
      type: Number,
      default: 5,
    },
    // Pinned mode only; defaults to `columns`.
    columnsMd: {
      type: Number,
      default: null,
    },
    minTileWidth: {
      type: Number,
      default: 140,
    },
    // Optional continuous sizing from grid density (0–100 mapped sizes).
    rowHeight: {
      type: Number,
      default: null,
    },
    thumbMax: {
      type: Number,
      default: null,
    },
    folderIconSize: {
      type: Number,
      default: null,
    },
    gap: {
      type: Number,
      default: 12,
    },
    itemProps: {
      type: Object,
      default: () => ({}),
    },
    itemPropsFor: {
      type: Function,
      default: null,
    },
    // Fluid mode only: step the column count down with the viewport instead of pure auto-fill.
    responsive: {
      type: Boolean,
      default: false,
    },
    sortable: {
      type: Boolean,
      default: false,
    },
    sortableOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    isFluid() {
      return this.columns === null;
    },
    hasDensitySizing() {
      return this.rowHeight != null || this.thumbMax != null;
    },
    wrapperClass() {
      return {
        'c-item-grid--fluid': this.isFluid,
        'c-item-grid--responsive': this.isFluid && this.responsive,
        'c-item-grid--pinned': !this.isFluid,
        'c-item-grid--density': this.hasDensitySizing,
      };
    },
    // Draggable-only bindings: a plain div would serialise `value` into a DOM attribute.
    wrapperProps() {
      return this.sortable ? { tag: 'div', value: this.items, ...this.sortableOptions } : {};
    },
    wrapperListeners() {
      return this.sortable
        ? {
          input: (value) => this.$emit('update:items', value),
          end: (event) => this.$emit('sort-end', event),
        }
        : {};
    },
    gridStyle() {
      const style = { gap: `${this.gap}px`, '--c-item-grid-min': `${this.minTileWidth}px` };

      // The track lives in CSS: an inline `grid-template-columns` would outrank the media queries.
      if (!this.isFluid) {
        style['--c-item-grid-columns'] = this.columns;
        style['--c-item-grid-columns-md'] = this.columnsMd ?? this.columns;
      }

      if (this.rowHeight != null) {
        style.gridTemplateRows = 'none';
        style.gridAutoRows = `${this.rowHeight}px`;
      }

      if (this.thumbMax != null) {
        style['--c-item-thumb-max'] = `${this.thumbMax}px`;
      }

      if (this.folderIconSize != null) {
        const icon = this.folderIconSize;
        style['--c-item-folder-icon'] = `${icon}px`;
        style['--c-item-folder-icon-h'] = `${Math.round(icon * 0.82)}px`;
        style['--c-item-folder-preview-height'] = `${Math.round(icon * 0.82) + 20}px`;
      }

      return style;
    },
  },
  methods: {
    propsFor(item) {
      return this.itemPropsFor
        ? { ...this.itemProps, ...this.itemPropsFor(item) }
        : this.itemProps;
    },
  },
};
</script>

<style scoped>
/* `minmax(0, 1fr)` rather than `1fr`: `1fr` tracks default to `min-width: auto`, so a wide tile overflows. */
.c-item-grid {
  display: grid;
  padding: 4px 4px 0;
}

.c-item-grid > * {
  min-width: 0;
}

/* auto-fill keeps empty tracks so sparse rows (e.g. 2 folders) do not stretch cards. */
.c-item-grid--fluid {
  grid-template-columns: repeat(auto-fill, minmax(var(--c-item-grid-min, 140px), 1fr));
}

.c-item-grid--pinned {
  grid-template-columns: repeat(var(--c-item-grid-columns, 5), minmax(0, 1fr));
}

@media (max-width: 991.98px) {
  .c-item-grid--pinned {
    grid-template-columns: repeat(var(--c-item-grid-columns-md, var(--c-item-grid-columns, 5)), minmax(0, 1fr));
  }
}

@media (min-width: 2300px) {
  .c-item-grid--responsive { grid-template-columns: repeat(9, minmax(var(--c-item-grid-min, 140px), 1fr)); }
}
@media (max-width: 2300px) {
  .c-item-grid--responsive { grid-template-columns: repeat(8, minmax(var(--c-item-grid-min, 140px), 1fr)); }
}
@media (max-width: 2100px) {
  .c-item-grid--responsive { grid-template-columns: repeat(7, minmax(var(--c-item-grid-min, 140px), 1fr)); }
}
@media (max-width: 1900px) {
  .c-item-grid--responsive { grid-template-columns: repeat(6, minmax(var(--c-item-grid-min, 140px), 1fr)); }
}
@media (max-width: 1700px) {
  .c-item-grid--responsive { grid-template-columns: repeat(5, minmax(var(--c-item-grid-min, 140px), 1fr)); }
}
@media (max-width: 1500px) {
  .c-item-grid--responsive { grid-template-columns: repeat(4, minmax(var(--c-item-grid-min, 140px), 1fr)); }
}
@media (max-width: 1300px) {
  .c-item-grid--responsive { grid-template-columns: repeat(3, minmax(var(--c-item-grid-min, 140px), 1fr)); }
}
@media (max-width: 1000px) {
  .c-item-grid--responsive { grid-template-columns: repeat(2, minmax(var(--c-item-grid-min, 140px), 1fr)); }
}

/* Continuous density: size tiles from column width. File previews stay square; names sit below. */
.c-item-grid--density >>> .c-grid-view-item {
  height: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.c-item-grid--density >>> .c-grid-view-item__preview {
  flex: 0 0 auto;
  min-height: 0;
}

.c-item-grid--density >>> .c-grid-view-item__preview--item {
  height: 100%;
  min-height: 0;
}

/* Folders size to the icon + one-line caption; stretch so every slot in a row matches. */
.c-item-grid--density.folder-items {
  grid-auto-rows: auto !important;
  align-items: stretch;
}

.c-item-grid--density.folder-items >>> .c-grid-view-item {
  height: 100%;
}

.c-item-grid--density.folder-items >>> .c-grid-view-item__preview {
  flex: 0 0 auto;
}

.c-item-grid--density.folder-items >>> .c-grid-view-item__preview--item {
  height: auto;
  min-height: 0;
}

.c-item-grid--density >>> .c-grid-view-item--folder .c-grid-view-item__preview--item {
  justify-content: flex-start;
}

.c-item-grid--density >>> .c-grid-view-item--folder .c-file-browser__item {
  flex: 0 0 var(--c-item-folder-preview-height, 88px);
  height: var(--c-item-folder-preview-height, 88px);
}
</style>
